import { call, put, takeLatest } from 'typed-redux-saga';

import { type CustomerProfile } from '@/customerManagement/interfaces/Customer';
import { getPlatform, isB2B } from '@/domains/core/settings/utils';
import { waitForAndGetCustomerProfile } from '@/domains/customerManagement/customerProfile/saga';
import {
  ADD_TO_CART_FAILED,
  ADD_TO_CART_REQUEST,
  ADD_TO_CART_SUCCESS,
  showAddToCartModal,
  showCrossPlatformPurchaseBlockedModal,
  type AddToCartRequestAction,
} from '@/shoppingCart/AddToCart/AddToCart.actions';
import { type TrackedProductDetails } from '@/shoppingCart/common/interfaces/TrackedProductDetails';
import { getMicroServiceError } from '@/shoppingCart/common/utils/getMicroServiceError';
import { addToCartV2 } from '@/shoppingCart/services/msfCart/addToCart';

function* addToCartCartSaga({ payload }: AddToCartRequestAction) {
  try {
    const {
      products,
      showConfirmationMessage = true,
      playlistReconciliation,
    } = payload;

    if (isB2B()) {
      // Block B2B Cross platform purchases
      const profile = (yield waitForAndGetCustomerProfile()) as CustomerProfile;
      if (profile.platform.toLowerCase() !== getPlatform()) {
        return yield* put(showCrossPlatformPurchaseBlockedModal());
      }
    }

    const { savedProductsId, errors, productsTotalPrice } = yield* call(
      addToCartV2Handler,
      products,
    );
    const allProductsHaveBeenSaved = products.length === savedProductsId.length;

    if (allProductsHaveBeenSaved) {
      yield* put({
        type: ADD_TO_CART_SUCCESS,
        payload: {
          products,
          savedProductIds: savedProductsId,
          showConfirmationMessage,
          productsTotalPrice,
          playlistReconciliation,
        },
      });
      return yield* put(showAddToCartModal({ showConfirmationMessage }));
    }

    // handles custom errors
    yield* put({
      type: ADD_TO_CART_FAILED,
      errors,
      payload: { savedProductIds: savedProductsId },
    });
    return yield* put(showAddToCartModal({ showConfirmationMessage }));
  } catch (error) {
    const { showConfirmationMessage = true } = payload;
    yield* put({
      type: ADD_TO_CART_FAILED,
      errors: error,
      payload: { savedProductIds: [] },
    });
    yield* put(showAddToCartModal({ showConfirmationMessage }));
  }
}

interface Response {
  failedProductIds?: number[];
  errors: { appCode: null | string; id: number }[];
  productsTotalPrice: number;
  savedProductsId: number[];
}

function* addToCartV2Handler(products: TrackedProductDetails[]) {
  let errors: Response['errors'] = [];
  try {
    const res = yield* call(addToCartV2, products);
    let savedProductsId = products.map((product) => product.id);
    if (res?.errorNotAdded) {
      const errorIds = res.errorNotAdded;
      savedProductsId = savedProductsId.filter((id) => !errorIds.includes(id));
      errors = res.errorNotAdded.map((id) => ({ appCode: '', id }));
    }
    return {
      failedProductIds: res.errorNotAdded || [],
      errors,
      productsTotalPrice: res.cart.main.productsTotalPrice,
      savedProductsId,
    };
  } catch (error) {
    const appCode = getMicroServiceError(error);
    errors = products.map(({ id }) => ({ appCode, id }));
  }
  // if call is not successful we want to return a custom error
  return {
    failedProductIds: products.map((product) => product.id),
    errors,
    productsTotalPrice: 0,
    savedProductsId: [],
  };
}

export function* watchAddToCartSaga() {
  yield* takeLatest(ADD_TO_CART_REQUEST, addToCartCartSaga);
}
